import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { useHiveClient } from '@gitbook/hive-react';
import { PalettePositionWidth, typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { renderIcon, TextInput, type TextInputProps } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { useContentReferenceContext } from './context';
import { createInputContentRefPalette } from './createInputContentRefPalette';
import type { UIResolvedContentReference } from './types';
import { useContentReference } from './useContentReference';

/**
 * Input to edit a content reference.
 */
export type ContentReferenceInputProps = {
    size?: TextInputProps['size'];
    value: api.ContentRef | null | undefined;
    placeholder?: string;
    onChangeValue: (ref: api.ContentRef | null) => void;
    /**
     * List of accepted references.
     */
    accept?: ContentRefKind[];
    disabled?: boolean;
    feedback?: ContentReferenceInputFeedback;
    clearable?: boolean;
    autoFocus?: boolean;
};

type ContentReferenceInputFeedback = TextInputProps['feedback'] | { type: 'none' };

const DEFAULT_SIZE = 'large';

/**
 * Input that allow to pick a content reference.
 */
export function ContentReferenceInput(props: ContentReferenceInputProps): React.ReactElement {
    return (
        <React.Suspense
            fallback={<TextInput disabled value="Loading..." size={props.size ?? DEFAULT_SIZE} />}
        >
            <ContentReferenceInputInner {...props} />
        </React.Suspense>
    );
}

/**
 * Inner component that suspends.
 */
function ContentReferenceInputInner(props: ContentReferenceInputProps): React.ReactElement {
    const {
        value,
        onChangeValue,
        placeholder,
        accept,
        feedback,
        size = DEFAULT_SIZE,
        disabled = false,
        clearable = true,
        autoFocus = false,
    } = props;

    // Resolves the content ref, it suspends
    const resolved = useContentReference(value);

    // References to DOM elements
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // The string representation of the value provided.
    const strValue = React.useMemo(
        () => getStringFromContentRef(value, resolved),
        [value, resolved]
    );

    // Input value kept synced with the value provided
    // so when the user selects it, it resets
    const [inputValue, setInputValue] = useStateWithDeps(strValue, [strValue]);

    // Reset the input value to its initial state.
    const resetInputValue = () => setInputValue(strValue);

    // We consider the input dirty if the user has typed
    // something difference from the initial value.
    const isDirty = inputValue !== strValue;

    // If dirty, the query is the input value, else it the empty
    // because we want to show the complete list.
    const query = isDirty ? inputValue : '';

    // Called when the user is done editing
    // - The palette is closed and the input is not focused
    const onDone = () => {
        resetInputValue();
        const parsedHref = normalizeUrlLikeStringToUrl(query);
        if (parsedHref) {
            onChangeValue({ kind: 'url', url: parsedHref });
        }
    };

    // Handle to control the palette.
    const paletteHandle = usePaletteHandle(containerRef, {
        onClose: () => {
            // If the input is not focused, reset.
            if (document.activeElement !== inputRef.current) {
                onDone();
            }
        },
        inputRef,
    });

    // Dependencies to create the palette.
    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();

    // Set a query into the palette, open it if not opened.
    const setPaletteQuery = (query: string) => {
        if (!paletteHandle.opened) {
            const isTypingURL = query.startsWith('http') || query.startsWith('www');
            paletteHandle.open(
                typeQueryInPalette(
                    createInputContentRefPalette(
                        onChangeValue,
                        contentReferenceContext,
                        hiveClient,
                        {
                            search: { enabled: false },
                            position: { width: PalettePositionWidth.ANCHOR },
                            accept: isTypingURL ? ['url'] : accept,
                        }
                    ),
                    query
                )
            );
        } else {
            paletteHandle.typeQuery(query);
        }
    };

    return (
        <div ref={containerRef} className="flex">
            <TextInput
                ref={inputRef}
                data-testid="contentRef.input"
                size={size}
                value={inputValue}
                onChange={(event) => {
                    const input = event.target.value;
                    setInputValue(input);
                    setPaletteQuery(input !== strValue ? input : '');
                }}
                onFocus={() => {
                    setPaletteQuery(query);
                }}
                onBlur={() => {
                    if (!paletteHandle.opened) {
                        onDone();
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setPaletteQuery(query);
                    }
                }}
                placeholder={placeholder}
                disabled={disabled}
                feedback={feedback?.type === 'none' ? undefined : feedback}
                autoFocus={autoFocus}
                onClear={
                    clearable && value
                        ? () => {
                              setInputValue('');
                              onChangeValue(null);
                              inputRef.current?.focus();
                          }
                        : undefined
                }
                prefix={
                    value && !isDirty && resolved.icon ? (
                        <span className="flex items-center text-secondary">
                            {renderIcon(resolved.icon, {
                                size: 16,
                                className: 'text-secondary',
                            })}
                        </span>
                    ) : undefined
                }
            />
        </div>
    );
}

/**
 * Get a string value from a content ref.
 */
function getStringFromContentRef(
    contentRef: api.ContentRef | null | undefined,
    resolved: UIResolvedContentReference
) {
    if (!contentRef) {
        return '';
    }

    if (contentRef.kind === 'anchor' || contentRef.kind === 'url') {
        return resolved.href;
    }

    return resolved.title;
}
